import { AuthorizationType } from 'consts';
import { mobileAndTabletCheck } from 'helpers/commonHelper';
import React, { createContext, PureComponent } from 'react';
import { IError } from 'types/Payment';

interface IProps {
  children: JSX.Element;
}

const initialValue = {
  authorizationType: AuthorizationType.REDIRECT,
  errors: [],
  formData: {},
  isMobileOrTablet: false,
  paymentMethodId: '',
  pmpt: 0,
  paymentMethodType: '',
  progress: 0,
  setAuthorizationType: (_: IAuthorizationType) => _,
  setErrors: (_: IError[]) => _,
  setFormData: (_: IFormData) => _,
  setPaymentMethod: () => undefined,
  setPaymentMethodAndProgress: () => undefined,
  setPaymentMethodAndProgressAndNotification: () => undefined,
  setProgress: () => undefined,
  showPaymentNotification: false,
  showStatus: true,
  isTokenizationEnabled: false,
  isSelectedPaymentMethodDisabled: false,
  selectedPaymentMethodDisabledReason: undefined,
};

export const AppContext = createContext<Partial<IContext>>(initialValue);

export class AppProvider extends PureComponent<IProps, Partial<IContext>> {
  constructor(props: IProps | Readonly<IProps>) {
    super(props);
    this.state = {
      authorizationType: AuthorizationType.REDIRECT,
      errors: [],
      formData: {},
      isMobileOrTablet: mobileAndTabletCheck(),
      paymentMethodId: '',
      pmpt: 0,
      paymentMethodType: '',
      progress: 0,
      showPaymentNotification: false,
      showStatus: true,
      isTokenizationEnabled: false,
      isTokenizedPayment: false,
      isSelectedPaymentMethodDisabled: false,
      selectedPaymentMethodDisabledReason: undefined,
    };
  }

  componentDidMount() {
    if ('onorientationchange' in window) {
      window.addEventListener('orientationchange', this.onOrientationChange, false);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('orientationchange', this.onOrientationChange);
  }

  onOrientationChange = () => {
    this.setState({
      isMobileOrTablet: mobileAndTabletCheck(),
    });
  };

  setProgressAndFormData = (formData: IFormData, progress = 0) => {
    if (progress >= 0 && progress <= 100) {
      this.setState({ progress, formData });
    }
  };

  setProgress = (progress = 0) => {
    if (progress >= 0 && progress <= 100) {
      this.setState({ progress });
    }
  };

  setFormData = (formData: IFormData) => {
    this.setState({ formData });
  };

  setErrors = (errors: IError[]) => {
    this.setState({ errors });
  };

  setPaymentMethod = (
    paymentMethodId: string,
    paymentMethodType: string,
    pmpt: number,
    showPaymentNotification: boolean
  ) => {
    this.setState({ paymentMethodId, paymentMethodType, pmpt, showPaymentNotification });
  };

  setPaymentMethodAndProgress = (paymentMethodId: string, paymentMethodType: string, pmpt: number, progress = 0) => {
    this.setState({ paymentMethodId, paymentMethodType, pmpt, progress });
  };

  setPaymentMethodAndProgressAndNotification = (
    paymentMethodId: string,
    paymentMethodType: string,
    pmpt: number,
    showPaymentNotification: boolean,
    progress = 0
  ) => {
    this.setState({ paymentMethodId, paymentMethodType, pmpt, progress, showPaymentNotification });
  };

  setAuthorizationType = ({ authorizationType }: IAuthorizationType) => {
    this.setState({ authorizationType });
  };

  setTokenizationEnabled = (isTokenizationEnabled: boolean) => {
    this.setState({ isTokenizationEnabled });
  };

  setTokenizedPayment = (isTokenizedPayment: boolean) => {
    this.setState({ isTokenizedPayment });
  };

  setSelectedPaymentMethodDisabledAndReason = (
    isSelectedPaymentMethodDisabled: boolean,
    selectedPaymentMethodDisabledReason: string
  ) => {
    this.setState({ isSelectedPaymentMethodDisabled, selectedPaymentMethodDisabledReason });
  };

  setTokenizedPaymentAndProgress = (
    formData: IFormData,
    isTokenizedPayment: boolean,
    paymentMethodId: string,
    paymentMethodType: string,
    pmpt: number,
    progress?: number
  ) => {
    this.setState({ formData, isTokenizedPayment, paymentMethodId, paymentMethodType, pmpt, progress });
  };

  render() {
    const { Provider } = AppContext;
    const { children } = this.props;
    return (
      <Provider
        value={{
          ...this.state,
          setAuthorizationType: this.setAuthorizationType,
          setErrors: this.setErrors,
          setFormData: this.setFormData,
          setPaymentMethod: this.setPaymentMethod,
          setPaymentMethodAndProgress: this.setPaymentMethodAndProgress,
          setPaymentMethodAndProgressAndNotification: this.setPaymentMethodAndProgressAndNotification,
          setProgress: this.setProgress,
          setTokenizationEnabled: this.setTokenizationEnabled,
          setTokenizedPayment: this.setTokenizedPayment,
          setTokenizedPaymentAndProgress: this.setTokenizedPaymentAndProgress,
          setSelectedPaymentMethodDisabledAndReason: this.setSelectedPaymentMethodDisabledAndReason,
          setProgressAndFormData: this.setProgressAndFormData,
        }}
      >
        {children}
      </Provider>
    );
  }
}
